import { type EndpointType } from './endpoint_metadata'
import { displayNode } from './display'

interface ResourceTypeElements {
  kindRadios: HTMLInputElement[]
  kindOptions?: HTMLElement
  kindHidden?: HTMLInputElement
  nodeSelect: HTMLSelectElement
  nodeInput?: HTMLElement
  nodeHidden?: HTMLInputElement
}

export class ResourceTypeSection {
  constructor (private readonly el: ResourceTypeElements) {}

  selectedKind (): string {
    return this.el.kindRadios.find(r => r.checked)?.value ?? ''
  }

  selectedNode (): string {
    return this.el.nodeSelect.value
  }

  autoselect (endpoint: EndpointType): void {
    const kinds = this.availableKinds(endpoint)
    const selected = this.selectedKind()
    const nextKind = kinds.includes(selected) ? selected : (kinds[0] ?? '')

    this.el.kindRadios.forEach((radio) => {
      radio.checked = radio.value === nextKind
    })

    this.syncKindInputs(nextKind, kinds.length === 1)
  }

  updateNodes (endpoint: EndpointType): string {
    const select = this.el.nodeSelect
    const initial = select.dataset.initialNodeValue
    const nodes = this.nodesForSelectedKind(endpoint)

    select.replaceChildren()
    nodes.forEach(node => select.add(new Option(displayNode(node), node)))

    const hasInitial = typeof initial === 'string' && initial.length > 0 && nodes.includes(initial)
    select.value = hasInitial ? initial : (nodes[0] ?? '')

    delete select.dataset.initialNodeValue

    this.syncNodeInputs(nodes)

    return select.value
  }

  syncNodeInputsFor (endpoint: EndpointType | null): void {
    const nodes = endpoint == null ? [] : this.nodesForSelectedKind(endpoint)
    this.syncNodeInputs(nodes)
  }

  private nodesForSelectedKind (endpoint: EndpointType): string[] {
    return this.selectedKind() === 'collection'
      ? endpoint.nodes_by_resource_type.collection
      : endpoint.nodes_by_resource_type.single
  }

  private availableKinds (endpoint: EndpointType): string[] {
    const kinds: string[] = []
    const nodes = endpoint.nodes_by_resource_type

    if (Array.isArray(nodes?.single) && nodes.single.length > 0) kinds.push('resource')
    if (Array.isArray(nodes?.collection) && nodes.collection.length > 0) kinds.push('collection')

    return kinds.length > 0 ? kinds : ['resource']
  }

  private syncKindInputs (kind: string, disabled: boolean): void {
    this.el.kindRadios.forEach((radio) => {
      radio.disabled = disabled
    })

    if (this.el.kindHidden != null) {
      this.el.kindHidden.value = kind
      this.el.kindHidden.disabled = !disabled
    }

    this.el.kindOptions?.classList.toggle('hidden', disabled)
  }

  private syncNodeInputs (nodes: string[]): void {
    const disabled = nodes.length <= 1
    this.el.nodeSelect.disabled = disabled

    if (this.el.nodeHidden != null) {
      this.el.nodeHidden.value = this.el.nodeSelect.value
      this.el.nodeHidden.disabled = !disabled
    }

    this.el.nodeInput?.classList.toggle('hidden', disabled)
  }
}
